/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { color, layout } from 'styled-system';
import base64 from 'base-64';
import { useDispatch, useSelector } from 'react-redux';
import { getAggrRequest } from 'redux-api/action/aggr';
import { topLineCsvRequest } from 'redux-api/action/csv';
import selectTopChart from 'redux-api/reselect/top-chart';
import ContentLoader from 'theme/content-loader';
import EmptyData from 'theme/empty-data';
import BtnLink from 'theme/buttons/btn-link';
import FiletypeXlsx from 'theme/icons/filetype-xlsx';
import translations from 'translations';
import Row from './row';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;

  & > .head {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  ${layout}
`;

const WrapEmpty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  ${color}
`;

function ListChart({
  compare,
  term,
  dataType,
  dateFrom,
  dateTo,
  dateRange,
  filters,
  terms,
  size,
  color,
  reloadChecked,
  noPrevious,
  topList,
  isAdmin,
}) {
  const [queriedKey, setQueriedKey] = useState(null);

  const dispatch = useDispatch();

  const { currency, languageCode } = useSelector((state) => state.config);
  const resultPayload = useSelector((state) => state.aggr.payload[queriedKey]);
  let resultTopChart = useSelector((state) => selectTopChart(state, queriedKey));
  const { analytics_type } = useSelector((state) => state.profile.payload);

  if (size === 'small') resultTopChart = resultTopChart.slice(0, 5);

  const query = useMemo(() => {
    if (!dataType || !dateTo || !dateFrom) return [];

    let result = `currency=${currency}&dataType=${dataType}&term=${term}&size=100&dateTo=${dateTo}&dateFrom=${dateFrom}&dateRange=${dateRange}&compare=${compare}&version=7&enterprise=${
      analytics_type || 'standard'
    }&isAdmin=${isAdmin}`;

    if (filters) {
      result = `${result}&filters=${filters}`;
    }

    if (terms) {
      result = `${result}&terms=${terms}`;
    }

    return result;
  }, [
    term,
    dateTo,
    dateFrom,
    dateRange,
    dataType,
    filters,
    terms,
    currency,
    compare,
    analytics_type,
    isAdmin,
  ]);

  useEffect(() => {
    const key = base64.encode(query);
    setQueriedKey(key);

    dispatch(
      getAggrRequest({
        term,
        size: 0,
        key,
        query,
        currency,
        isAdmin,
      }),
    );
  }, [dispatch, query, currency, term, reloadChecked, isAdmin]);

  const handleExportCsv = useCallback(() => {
    dispatch(
      topLineCsvRequest({
        dateFrom,
        dateTo,
        currency,
        languageCode,
        term,
        query,
        isAdmin,
      }),
    );
  }, [dispatch, dateFrom, dateTo, query, term, currency, languageCode, isAdmin]);

  const isWaiting = useMemo(() => {
    if (!resultPayload) return true;

    return resultPayload.isWaiting;
  }, [resultPayload]);

  const isEmpty = useMemo(() => {
    if (isWaiting) return false;

    return resultTopChart.length === 0;
  }, [isWaiting, resultTopChart]);

  return (
    <Wrapper maxHeight={isEmpty ? '100%' : '660px'}>
      {isWaiting ? (
        <ContentLoader
          width={470}
          height={200}
          lines={[
            {
              x: 0,
              y: 0,
              rx: 5,
              ry: 5,
              width: 80,
              height: 12,
            },
            {
              x: 0,
              y: 20,
              rx: 5,
              ry: 5,
              width: 120,
              height: 12,
            },
            {
              x: 0,
              y: 60,
              rx: 5,
              ry: 5,
              width: 300,
              height: 12,
            },
            {
              x: 0,
              y: 100,
              rx: 5,
              ry: 5,
              width: 80,
              height: 12,
            },
            {
              x: 0,
              y: 120,
              rx: 5,
              ry: 5,
              width: 120,
              height: 12,
            },
            {
              x: 0,
              y: 160,
              rx: 5,
              ry: 5,
              width: 300,
              height: 12,
            },
          ]}
        />
      ) : isEmpty ? (
        <WrapEmpty bg="primary_50">
          <EmptyData label={translations[languageCode]['no available']} />
        </WrapEmpty>
      ) : (
        <>
          {topList !== 'partners' && size !== 'small' && (
            <div className="head">
              <BtnLink
                icon={<FiletypeXlsx />}
                label={translations[languageCode]['Export XLS']}
                onClick={handleExportCsv}
              />
            </div>
          )}
          {resultTopChart.map((payload, index) => (
            <Row
              key={payload.title}
              payload={payload}
              index={index}
              languageCode={languageCode}
              dataType={dataType}
              size={size}
              color={color}
              noPrevious={noPrevious}
            />
          ))}
        </>
      )}
    </Wrapper>
  );
}

export default ListChart;
